import { isNotDefined } from '@sgme/fp';

import { selectCurrencyPairData } from '@/api/config/selectors.ts';
import { tradeCaptureApi } from '@/api/trade-capture';
import type {
  CheckOptionParams,
  NullableHedges,
  NullableLegProperties,
  NullableOptionProperties,
} from '@/api/trade-capture/option/checkOption.ts';
import { optionSlice } from '@/store/option/slice.ts';
import type { FxOption, OptionProductName, OptionProperties, PremiumType } from '@/store/option/state.ts';
import { shallowMergeLegs } from '@/store/option/utils.ts';
import type { AppState } from '@/store/store.ts';
import { createAppAsyncThunk } from '@/store/storeMethods.ts';

/**
 * Calls TC with the diff passed as arguments.
 * For convenience, you can pass a Partial of the tile's properties, of its legs, of its hedges, or a mix of the three.
 */
type UpdateCheckOptionThunkParams = {
  type?: 'update';
  tileId: string;
  productName?: OptionProductName;
  properties?: NullableOptionProperties;
  legProperties?: NullableLegProperties | NullableLegProperties[];
  hedges?: NullableHedges | NullableHedges[];
};

/**
 * Used when the option is reset to an initial state (reset/addTile+changeCurrency/restoreWorkspace/duplicate).
 * Uses either the initialProperties passed as argument or the tile current state to make the TC call.
 */
type InitCheckOptionThunkParams = {
  type: 'init';
  tileId: string;
  productName?: OptionProductName;
  initialProperties?: OptionProperties;
  shouldApplyUserPrefs: boolean;
};

export type CheckOptionThunkParams = UpdateCheckOptionThunkParams | InitCheckOptionThunkParams;

type RequiredParams = {
  legs: NullableLegProperties[];
  hedges: NullableHedges[];
  properties?: NullableOptionProperties;
  productName: OptionProductName;
  shouldApplyUserPrefs: boolean;
};

export const checkOptionThunk = createAppAsyncThunk<void, CheckOptionThunkParams>(
  'option/checkOptionThunk',
  async (params, { dispatch, getState }) => {
    const optionTile = optionSlice.selectors.getOptionTile(getState(), params.tileId);
    if (isNotDefined(optionTile)) {
      return;
    }

    const { tileId } = params;
    const { legs, hedges, properties, productName, shouldApplyUserPrefs } = getRequiredParams(params, optionTile);

    const optionProperties = optionTile.properties;
    const tradeCapture = params.type === 'init' ? { ...optionTile.tradeCapture, version: -1 } : optionTile.tradeCapture;
    const product = productName ?? optionTile.type;

    // merge legs: since we send a delta to TC, we need to reconstitute the full legs array, because TC needs all legs
    const legProperties = shallowMergeLegs(optionProperties.legs, legs);
    const mergedHedges = shallowMergeLegs(optionProperties.hedges, hedges);

    const currencyPair = properties?.currencyPair ?? undefined;
    const finalProperties = shouldApplyUserPrefs
      ? {
          ...properties,
          ...getDefaultsFromUserPrefs(getState(), currencyPair),
        }
      : properties;

    const query: CheckOptionParams = {
      tileId,
      tradeCapture,
      productName: product,
      properties: finalProperties,
      legProperties,
      hedges: mergedHedges,
      optionState: optionTile,
    };

    await dispatch(tradeCaptureApi.endpoints.checkOption.initiate(query));
  },
);

function getRequiredParams(params: CheckOptionThunkParams, option: FxOption): RequiredParams {
  switch (params.type) {
    case undefined:
    case 'update': {
      const { legProperties: legs = [], hedges = [], properties } = params;
      const productName = params.productName ?? option.type;
      return {
        legs: Array.isArray(legs) ? legs : [legs],
        hedges: Array.isArray(hedges) ? hedges : [hedges],
        properties,
        productName,
        shouldApplyUserPrefs: false,
      };
    }

    case 'init': {
      const optionProperties = params.initialProperties ?? option.properties;
      const productName = params.productName ?? option.type;
      return {
        productName,
        properties: optionProperties,
        hedges: optionProperties.hedges,
        legs:
          productName === 'Trigger' || productName === 'Binary'
            ? optionProperties.legs.map((leg) => ({ ...leg, barrier1: leg.barrier1 ?? {} }))
            : optionProperties.legs,
        shouldApplyUserPrefs: params.shouldApplyUserPrefs,
      };
    }
  }
}

function getDefaultsFromUserPrefs(state: AppState, currencyPair: string | undefined): Partial<OptionProperties> {
  const userPreferences = state.userPreferences;
  const currencyPairData = selectCurrencyPairData(state, currencyPair);
  const isNonDeliverable = currencyPairData?.notDeliverable ?? false;
  let hedgeType = userPreferences.defaultHedgeType ?? 'Live';
  // TC error, they should not acknowledge hedge types on NDO...
  if (isNonDeliverable) {
    hedgeType = 'Live';
  }
  const premiumCategory = userPreferences.defaultPriceType ?? 'AMOUNT';
  const premiumType: PremiumType = { premiumCurrency: 1, premiumCategory: premiumCategory };
  return { hedgeType, premiumType };
}
